/**
 * The RT Hub Agent — the Windows/macOS application VAs clock in from — and the rules the web app
 * uses to talk about it.
 *
 * Why this exists: clocking in lives ONLY in the agent's tray menu. The web app is a different
 * program with a different job, but it is installable from Chrome ("Install app"), so a VA can end
 * up in a window that looks like an app, land on My Hours, and find nothing to press. These
 * helpers decide what My Hours says in that situation, and they are pure so the decision can be
 * tested without a browser.
 */
/**
 * ⛔ The ONE name for the desktop application in VA-facing copy. "the desktop app" is banned: the
 * web app is also a desktop app once Chrome installs it, which is exactly how a VA can read
 * "clock in from the desktop app" while already looking at the thing they think it means.
 */
export const AGENT_NAME = 'RT Hub Agent';
/** What the web app is called when it has to be distinguished from the agent. */
export const WEB_APP_NAME = 'RT Hub website';
/** The installer filename a manager sends, when there is no hosted copy to link to. */
export const AGENT_WINDOWS_INSTALLER = 'Remote Talent Hub Agent Setup.exe';
/** Where the tray icon lives on Windows — the single line the panel shows. */
export const AGENT_TRAY_HINT = 'Look for the violet RT dot in the Windows system tray, next to the clock — if it is not there, click the ^ arrow to show hidden icons.';
/**
 * Is this page running inside the RT Hub Agent?
 *
 * Two signals, deliberately, and the order matters:
 *
 * 1. **The preload flag.** `messages-preload.ts` runs before the page's own scripts and exposes
 *    `window.rthAgent = { isAgent: true }` over `contextBridge`. It is explicit, it is per page
 *    load, and nothing outside the agent can set it. This is the signal we want.
 * 2. **`Electron/…` in the user agent.** Needed because the agent is handed to VAs as an .exe by
 *    their manager and is not auto-updated: every copy already installed predates the preload flag
 *    and would otherwise be told to download itself. Electron puts its own token in the UA by
 *    default, and a normal Chrome/Edge browser — including the web app installed as a PWA — never
 *    carries it.
 *
 * A localStorage flag was rejected: the agent's preload already seeds localStorage for auth, so it
 * was the obvious place, but localStorage is sticky per origin. Once written it outlives the page,
 * the window and the session that wrote it, so it can only ever grow more wrong. A bridge
 * exposure cannot leak past the page load that made it.
 */
export function isRunningInAgent(input) {
    if (input.preloadFlag === true)
        return true;
    return /\bElectron\/[\d.]+/i.test(input.userAgent ?? '');
}
/**
 * Turn the configured download URL into a call to action.
 *
 * Unset, blank, or anything that is not an http(s) URL or a site-relative path yields
 * `ask-manager` — the panel then tells the VA to ask their manager for
 * `Remote Talent Hub Agent Setup.exe` and renders no button at all. ⛔ A button that goes nowhere
 * is worse than no button: it reads as "you have done the step" and sends the VA away satisfied.
 */
export function agentDownloadCta(url) {
    const href = (url ?? '').trim();
    if (!href)
        return { kind: 'ask-manager' };
    if (!/^https?:\/\//i.test(href) && !href.startsWith('/'))
        return { kind: 'ask-manager' };
    return { kind: 'download', href };
}
/**
 * Decide the My Hours panel.
 *
 * Inside the agent the panel is HIDDEN — the tray menu with Clock in on it is a couple of
 * centimetres away, and pointing at it from the page it is hosting is noise. Outside the agent the
 * panel always shows, because a VA looking at My Hours in a browser has no other way to find out
 * where clocking lives.
 */
export function clockInPanelView(input) {
    if (input.inAgent)
        return { kind: 'hidden' };
    const cta = agentDownloadCta(input.downloadUrl);
    return cta.kind === 'download' ? { kind: 'download', href: cta.href } : { kind: 'ask-manager' };
}
//# sourceMappingURL=vaAgent.js.map